import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  preofferSelected,
  isInvoicesFirstLoadVar,
} from '../../../apollo/reactive-variables/invoices';
import currencyVar from '../../../apollo/reactive-variables/money';
import { cleanPreOfferShoppingCart } from '../../../apollo/helpers/shopping-cart';
import { clearFiltersFromHistory } from '../../../helpers/url-filter-translator';
import useNewInvoiceCharged from '../../../hooks/useNewInvoiceCharged';
import useIsMobile from '../../../hooks/useIsMobile';
import useSelectedCompany from '../../../hooks/useSelectedCompany';
import useRequestCreditLineSublineAndPreOfferEvaluation from '../../../hooks/useRequestCreditLineSublineAndPreOfferEvaluation';
import useEvaluatePreoffers from '../../../hooks/useEvaluatePreoffers';
import { GTM_EVENTS } from '../../../constants/tag-manager-events';
import { pushCompanyConversionEvent } from '../../../helpers/tag-manager';

const useAvailableFactoringState = ({ enableCreditLineFlow = false } = {}) => {
  const history = useHistory();
  const selectedPreofferIds = useReactiveVar(preofferSelected);
  const isInvoicesFirstLoad = useReactiveVar(isInvoicesFirstLoadVar);
  const currency = useReactiveVar(currencyVar);
  const company = useSelectedCompany();
  const [newInvoices, readNewInvoices] = useNewInvoiceCharged();
  const isMobile = useIsMobile('tablet');

  const [showConfirmationSummary, setShowConfirmationSummary] = useState(false);
  const [showEvaluationSentDialog, setShowEvaluationSentDialog] = useState(false);
  const [creditLineErrorCode, setCreditLineErrorCode] = useState(null);
  const [debtorNames, setDebtorNames] = useState([]);
  const [warningCode, setWarningCode] = useState(null);

  useEffect(() => {
    if (selectedPreofferIds.length === 0) {
      setShowConfirmationSummary(false);
      setShowEvaluationSentDialog(false);
    }
  }, [selectedPreofferIds]);

  const handleAddMoreInvoicesButtonClick = useCallback(() => {
    setShowConfirmationSummary(false);
  }, []);

  const handleBackToQuote = useCallback(() => {
    setShowConfirmationSummary(false);
  }, []);

  const stepsCallbacks = useMemo(() => ({
    handleBackToQuote,
  }), [handleBackToQuote]);

  const handleEvaluateButtonClick = useCallback((mutationWarningCode) => {
    if (enableCreditLineFlow) {
      setWarningCode(mutationWarningCode || null);
    }
    setShowEvaluationSentDialog(true);
  }, [enableCreditLineFlow]);

  const handleCreditLineError = useCallback((errorCode, debtorNamesList = []) => {
    setCreditLineErrorCode(errorCode);
    setDebtorNames(debtorNamesList);
  }, []);

  const closeCreditLineErrorDialog = useCallback(() => {
    setCreditLineErrorCode(null);
    setDebtorNames([]);
  }, []);

  const handleEvaluateLineSuccess = useCallback((warningCodeFromMutation) => {
    closeCreditLineErrorDialog();
    setWarningCode(warningCodeFromMutation || null);
    setShowEvaluationSentDialog(true);
    pushCompanyConversionEvent(GTM_EVENTS.EVALUATION_REQUEST, company);
  }, [closeCreditLineErrorDialog, company]);

  const {
    requestCreditLineSublineAndPreOfferEvaluation,
    loading: evaluateLineLoading,
  } = useRequestCreditLineSublineAndPreOfferEvaluation(handleEvaluateLineSuccess);

  const handleEvaluateLineClick = useCallback(() => {
    const debtorRuts = debtorNames.map((debtor) => debtor.rut);
    requestCreditLineSublineAndPreOfferEvaluation(selectedPreofferIds, debtorRuts);
  }, [debtorNames, selectedPreofferIds, requestCreditLineSublineAndPreOfferEvaluation]);

  const handleEvaluateInvoicesSuccess = useCallback((mutationWarningCode) => {
    closeCreditLineErrorDialog();
    setWarningCode(mutationWarningCode || null);
    setShowEvaluationSentDialog(true);
  }, [closeCreditLineErrorDialog]);

  const {
    requestPreoffer,
    loading: evaluateInvoicesLoading,
  } = useEvaluatePreoffers(handleEvaluateInvoicesSuccess, handleCreditLineError);

  const dialogOnClose = useCallback(() => {
    setShowEvaluationSentDialog(false);
    if (enableCreditLineFlow) {
      setWarningCode(null);
    }
    cleanPreOfferShoppingCart();
    clearFiltersFromHistory(history);
    setTimeout(() => {
      history.push('/app/sales/factoring/evaluation');
    }, 0);
  }, [enableCreditLineFlow, history]);

  return {
    selectedPreofferIds,
    isInvoicesFirstLoad,
    currency,
    company,
    newInvoices,
    readNewInvoices,
    isMobile,
    showConfirmationSummary,
    setShowConfirmationSummary,
    showEvaluationSentDialog,
    handleAddMoreInvoicesButtonClick,
    handleEvaluateButtonClick,
    stepsCallbacks,
    dialogOnClose,
    warningCode,
    creditLineErrorCode,
    debtorNames,
    closeCreditLineErrorDialog,
    handleCreditLineError,
    handleEvaluateLineClick,
    evaluateLineLoading,
    requestPreoffer,
    evaluateInvoicesLoading,
  };
};

export default useAvailableFactoringState;
